import type { TranslationCatalog } from './types';

export const ar: TranslationCatalog = {
  localeName: 'العربية',
  dir: 'rtl',
  brand: { wordmark: 'please.repair', product: 'Please.repair' },
  nav: {
    signIn: 'تسجيل الدخول',
    tenants: 'للمستأجرين',
    advocates: 'للمناصرين',
    landlords: 'للمالكين والوكلاء',
    joinWaitlist: 'انضم إلى قائمة الانتظار',
    dashboard: 'لوحة التحكم', // drafted — not in design corpus
    knowledge: 'المعرفة', // drafted — not in design corpus
  },
  hero: {
    title: 'إصلاحات السكن المستأجر، تُنجز كما ينبغي.',
    subtitle:
      'يتابع Please.repair كل طلب إصلاح من أول بلاغ حتى إتمام العمل. وإذا تعثّر الطلب، يرشدك إلى الخطوة القانونية التالية في ولايتك أو إقليمك.',
    cta: 'انضم إلى قائمة الانتظار',
  },
  audiences: {
    tenant: {
      title: 'أنا مستأجر',
      blurb: 'أبلغ عن المشكلة، واحفظ أدلتك، واعرف ما تفعله إذا لم يتحرك أحد.',
    },
    advocate: {
      title: 'أساعد مستأجراً',
      blurb:
        'صديق أو قريب أو أخصائي اجتماعي أو مركز قانوني. أدر القضية إلى جانبه، بهدوء ووفق الأصول.',
    },
    landlord: {
      title: 'أنا مالك أو مدير عقارات',
      blurb: 'قائمة واحدة لكل الطلبات، مع سجل موثّق يبقيك ملتزماً وبعيداً عن جلسات التحكيم.',
    },
  },
  waitlist: {
    heading: 'احصل على Please.repair أولاً',
    emailLabel: 'بريدك الإلكتروني',
    submit: 'انضم إلى قائمة الانتظار',
    success: 'تم تسجيلك في القائمة. نتواصل معك قريباً.',
    finePrint: 'لا رسائل مزعجة، رسالة واحدة تقريباً في الشهر.',
  },
  signIn: {
    heading: 'سجّل دخولك أو أنشئ حسابك',
    stages: ['البريد', 'التحقق', 'الاتفاقيات', 'الأدوار', 'العقارات'],
    comingNote: 'التطبيق الكامل قادم قريباً. ما تراه هنا نسخة معاينة.', // drafted — not in design corpus
  },
  roles: {
    tenant: 'مستأجر',
    advocate: 'مناصر',
    propertyManager: 'مدير عقارات',
    owner: 'مالك',
    tradie: 'حرفي',
  },
  statuses: {
    open: 'مفتوحة',
    acknowledged: 'مستلمة',
    scheduled: 'مجدولة',
    awaitingQuote: 'بانتظار السعر',
    quoted: 'مسعّرة',
    overdue: 'متأخرة',
    escalated: 'مصعّدة',
    resolved: 'محلولة',
  },
  severities: { urgent: 'عاجلة', high: 'مرتفعة', routine: 'اعتيادية' },
  dashboard: {
    title: 'لوحة كل شيء',
    kpiOpen: 'قضايا مفتوحة',
    kpiDueWeek: 'تستحق هذا الأسبوع',
    kpiOverdue: 'متأخرة',
    kpiResolved30: 'محلولة · 30 يوماً',
    filterAll: 'الكل',
    filterOpen: 'مفتوحة',
    filterDueSoon: 'تستحق قريباً',
    filterOverdue: 'متأخرة',
    searchPlaceholder: 'ابحث في القضايا والعقارات والأشخاص',
    emptyQueue: 'لا شيء هنا بعد',
    timelineHeading: 'الخط الزمني',
    deadlineHeading: 'المهلة القانونية',
    sampleDataBadge: 'بيانات تجريبية', // drafted — not in design corpus
    chartTitle: 'القضايا المفتوحة حسب درجة الخطورة', // drafted — not in design corpus
    viewAsTable: 'اعرض كجدول', // drafted — not in design corpus
    viewAsChart: 'اعرض كرسم بياني', // drafted — not in design corpus
  },
  knowledge: {
    heading: 'المعرفة', // drafted — not in design corpus
    intro:
      'قواعد إصلاحات السكن المستأجر في كل ولاية وإقليم: قوائم الإصلاحات العاجلة والمهل الزمنية للإصلاح والهيئات وأين تجد المساعدة.', // ADR 0018 — neutral, matching clocksHeading
    disclaimer:
      'ينظّم Please.repair الوقائع ويرشدك إلى القواعد والنماذج الرسمية، ولا يقدّم استشارة قانونية.',
    lastVerified: 'آخر تحقق', // drafted — not in design corpus
    urgentHeading: 'الإصلاحات العاجلة', // drafted — not in design corpus
    clocksHeading: 'المهل الزمنية للإصلاح',
    bodiesHeading: 'الهيئات والجهات', // drafted — not in design corpus
    helpHeading: 'أين تجد المساعدة', // drafted — not in design corpus
    citationsHeading: 'المراجع',
    verifyFlagsHeading: 'علامات التحقق', // drafted — not in design corpus
    clockStatutory: 'قانوني · {period}',
    clockBenchmark: 'مرجع عملي · نحو {period}',
  },
  a11y: {
    skipToContent: 'الانتقال إلى المحتوى',
    opensInNewTab: 'يفتح في تبويب جديد',
    loading: 'جارٍ التحميل…', // drafted — not in design corpus
  },
  errors: {
    generic: 'حدث خطأ ما.', // drafted — not in design corpus
    retry: 'حاول مرة أخرى', // drafted — not in design corpus
    notFound: 'الصفحة غير موجودة', // drafted — not in design corpus
    notFoundBody: 'هذه الصفحة غير موجودة أو نُقلت.', // drafted — not in design corpus
    serverError: 'حدث خطأ ما', // drafted — #376
    serverErrorBody: 'تعذّر عرض هذه الصفحة. حاول مرة أخرى، أو ابدأ من الصفحة الرئيسية.', // drafted — #376
    offline: 'أنت غير متصل بالإنترنت', // drafted — #376
    offlineBody: 'يحتاج Please.repair إلى اتصال لتحميل هذه الصفحة. أعد الاتصال وحاول مرة أخرى.', // drafted — #376
  },
  meta: {
    home: {
      title: 'Please.repair — إصلاحات السكن المستأجر موثّقة',
      description:
        'Please.repair — إصلاحات السكن المستأجر موثّقة. أبلغ عن المشكلات، واحفظ الأدلة، وتابع المهل الزمنية للإصلاح، ونسّق بين كل الأطراف.',
    },
    tenants: {
      title: 'للمستأجرين — Please.repair',
      description: 'أبلغ عن المشكلة، واحفظ أدلتك، واعرف ما تفعله إذا لم يتحرك أحد.',
    },
    advocates: {
      title: 'للمناصرين — Please.repair',
      description:
        'صديق أو قريب أو أخصائي اجتماعي أو مركز قانوني. أدر القضية إلى جانبه، بهدوء ووفق الأصول.',
    },
    landlords: {
      title: 'للمالكين والوكلاء — Please.repair',
      description: 'قائمة واحدة لكل الطلبات، مع سجل موثّق يبقيك ملتزماً وبعيداً عن جلسات التحكيم.',
    },
    signIn: {
      title: 'تسجيل الدخول — Please.repair',
      description: 'سجّل دخولك أو أنشئ حسابك في Please.repair.',
    },
    dashboard: {
      title: 'لوحة التحكم — Please.repair',
      description: 'القضايا المفتوحة والمهل والنشاط الأخير في قائمة واحدة.',
    },
    knowledge: {
      title: 'قواعد إصلاحات السكن المستأجر حسب الولاية والإقليم — Please.repair',
      description:
        'قواعد إصلاحات السكن المستأجر في كل ولاية وإقليم: قوائم الإصلاحات العاجلة والمهل الزمنية للإصلاح والهيئات وأين تجد المساعدة.',
    },
    knowledgeDetail: {
      title: 'قواعد إصلاحات السكن المستأجر — Please.repair',
      description:
        'قوائم الإصلاحات العاجلة والمهل الزمنية للإصلاح والهيئات وأين تجد المساعدة، ولايةً ولايةً.',
    },
    offline: {
      title: 'أنت غير متصل بالإنترنت — Please.repair',
      description: 'يحتاج Please.repair إلى اتصال لتحميل هذه الصفحة. أعد الاتصال وحاول مرة أخرى.',
    },
    serverError: {
      title: 'حدث خطأ ما — Please.repair',
      description: 'تعذّر عرض هذه الصفحة. حاول مرة أخرى، أو ابدأ من الصفحة الرئيسية.',
    },
    notFound: {
      title: 'الصفحة غير موجودة — Please.repair',
      description: 'هذه الصفحة غير موجودة أو نُقلت.',
    },
  },
  footer: {
    disclaimer:
      'ينظّم Please.repair الوقائع ويرشدك إلى القواعد والنماذج الرسمية، ولا يقدّم استشارة قانونية.',
    rights: '© 2026 Aperim Pty Ltd · ABN 46 150 699 737',
  },
};
